<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'next/textarea/TextArea.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import RasLabelPicker from './RasLabelPicker.vue';

const props = defineProps({
  rule: {
    type: Object,
    default: null,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const dialogRef = ref(null);

const RULE_TYPES = ['info', 'menu', 'intake', 'journey', 'age_restriction'];
const MENU_ACTIONS = ['book', 'info', 'escalate', 'intake', 'add_label'];
const JOURNEY_KEYS = ['subsidiary', 'patient_age', 'going_today'];

const isEditing = computed(() => !!props.rule);

const buildInitialState = () => {
  const r = props.rule;
  if (!r) {
    return {
      rule_type: 'info',
      title: '',
      active: true,
      trigger: '',
      content: '',
      menu_question: '',
      menu_fallback: '',
      menu_options: [],
      intake_fields: [],
      intake_request_template: '',
      intake_closing_message: '',
      intake_fallback: '',
      intake_labels: [],
      journey_fields: [],
      journey_inline_allowed: '',
      journey_closing_message: '',
      age_min: '',
      age_max: '',
      age_message: '',
    };
  }

  return {
    rule_type: r.type || 'info',
    title: r.title || '',
    active: r.active !== undefined ? r.active : true,
    trigger: r.trigger || '',
    content: r.content || '',
    menu_question: r.menu_question || '',
    menu_fallback: r.menu_fallback || '',
    menu_options: (r.menu_options || []).map(opt => ({
      action: opt.action || 'book',
      specialties: (opt.specialties || []).join(', '),
      professional: opt.professional || '',
      motivo: opt.motivo || '',
      message: opt.message || '',
      rule_key: opt.rule_key || '',
      labels: opt.labels || [],
    })),
    intake_fields: (r.intake_fields || []).map(f => ({
      key: f.key || '',
      prompt: f.prompt || '',
    })),
    intake_request_template: r.intake_request_template || '',
    intake_closing_message: r.intake_closing_message || '',
    intake_fallback: r.intake_fallback || '',
    intake_labels: r.labels || [],
    journey_fields: (r.journey_fields || []).map(f => ({
      key: f.key,
      prompt: f.prompt || '',
    })),
    journey_inline_allowed: (r.journey_inline_allowed || []).join(', '),
    journey_closing_message: r.journey_closing_message || '',
    age_min:
      r.age_min !== null && r.age_min !== undefined ? String(r.age_min) : '',
    age_max:
      r.age_max !== null && r.age_max !== undefined ? String(r.age_max) : '',
    age_message: r.age_message || '',
  };
};

const state = reactive(buildInitialState());

const parseCommaList = str =>
  str
    ? str
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
    : [];

const buildRuleData = () => {
  switch (state.rule_type) {
    case 'info':
      return { trigger: state.trigger || null, content: state.content };
    case 'menu':
      return {
        trigger: state.trigger,
        menu_question: state.menu_question,
        menu_fallback: state.menu_fallback,
        menu_options: state.menu_options.map((opt, idx) => {
          const item = { index: idx + 1, action: opt.action };
          if (opt.action === 'book') {
            item.specialties = parseCommaList(opt.specialties);
            if (opt.professional) item.professional = opt.professional;
            if (opt.motivo) item.motivo = opt.motivo;
          } else if (opt.action === 'info') {
            item.message = opt.message;
          } else if (opt.action === 'intake') {
            item.rule_key = opt.rule_key;
          } else if (opt.action === 'add_label') {
            item.labels = opt.labels;
          }
          if (opt.action !== 'add_label' && opt.labels.length) {
            item.labels = opt.labels;
          }
          return item;
        }),
      };
    case 'intake':
      return {
        trigger: state.trigger,
        intake_fields: state.intake_fields.map(f => ({
          key: f.key,
          prompt: f.prompt,
        })),
        intake_request_template: state.intake_request_template,
        intake_closing_message: state.intake_closing_message,
        intake_fallback: state.intake_fallback,
        labels: state.intake_labels,
      };
    case 'journey':
      return {
        trigger: state.trigger,
        journey_fields: state.journey_fields.map(f => ({
          key: f.key,
          prompt: f.prompt,
        })),
        journey_inline_allowed: parseCommaList(state.journey_inline_allowed),
        journey_closing_message: state.journey_closing_message,
      };
    case 'age_restriction':
      return {
        age_min: state.age_min !== '' ? Number(state.age_min) : null,
        age_max: state.age_max !== '' ? Number(state.age_max) : null,
        age_message: state.age_message,
      };
    default:
      return {};
  }
};

const MENU_OPTION_RULES = [
  {
    invalid: opt =>
      opt.action === 'book' &&
      !opt.specialties.trim() &&
      !opt.professional.trim(),
    key: 'OPTION_BOOK_TARGET',
  },
  {
    invalid: opt => opt.action === 'info' && !opt.message.trim(),
    key: 'OPTION_INFO_MESSAGE',
  },
  {
    invalid: opt => opt.action === 'intake' && !opt.rule_key.trim(),
    key: 'OPTION_INTAKE_KEY',
  },
  {
    invalid: opt => opt.action === 'add_label' && !opt.labels.length,
    key: 'OPTION_ADD_LABEL',
  },
];

// Alerts on the first problem found and reports the option as invalid, so
// `every` short-circuits exactly where the original loop returned.
const isMenuOptionValid = (opt, i) => {
  const V = 'INTEGRATION_SETTINGS.RAS.RULE_FORM.VALIDATION';
  const broken = MENU_OPTION_RULES.find(rule => rule.invalid(opt));
  if (!broken) return true;

  useAlert(t(`${V}.${broken.key}`, { n: i + 1 }));
  return false;
};

const validate = () => {
  const V = 'INTEGRATION_SETTINGS.RAS.RULE_FORM.VALIDATION';
  if (!state.title.trim()) {
    useAlert(t(`${V}.TITLE_REQUIRED`));
    return false;
  }
  switch (state.rule_type) {
    case 'info':
      if (!state.content.trim()) {
        useAlert(t(`${V}.CONTENT_REQUIRED`));
        return false;
      }
      break;
    case 'menu':
      if (!state.trigger.trim()) {
        useAlert(t(`${V}.TRIGGER_REQUIRED`));
        return false;
      }
      if (!state.menu_question.trim()) {
        useAlert(t(`${V}.MENU_QUESTION_REQUIRED`));
        return false;
      }
      if (!state.menu_options.length) {
        useAlert(t(`${V}.MENU_OPTIONS_MIN`));
        return false;
      }
      if (!state.menu_options.every(isMenuOptionValid)) return false;
      break;
    case 'intake':
      if (!state.trigger.trim()) {
        useAlert(t(`${V}.TRIGGER_REQUIRED`));
        return false;
      }
      if (!state.intake_request_template.trim()) {
        useAlert(t(`${V}.INTAKE_TEMPLATE_REQUIRED`));
        return false;
      }
      if (!state.intake_fields.length) {
        useAlert(t(`${V}.INTAKE_FIELDS_MIN`));
        return false;
      }
      if (
        !state.intake_fields.every((f, i) => {
          if (f.key.trim() && f.prompt.trim()) return true;
          useAlert(t(`${V}.INTAKE_FIELD_INCOMPLETE`, { n: i + 1 }));
          return false;
        })
      ) {
        return false;
      }
      break;
    case 'journey':
      if (!state.trigger.trim()) {
        useAlert(t(`${V}.TRIGGER_REQUIRED`));
        return false;
      }
      if (!state.journey_fields.length) {
        useAlert(t(`${V}.JOURNEY_FIELDS_MIN`));
        return false;
      }
      if (
        !state.journey_fields.every((f, i) => {
          if (f.prompt.trim()) return true;
          useAlert(t(`${V}.JOURNEY_FIELD_INCOMPLETE`, { n: i + 1 }));
          return false;
        })
      ) {
        return false;
      }
      break;
    case 'age_restriction':
      if (state.age_min === '' && state.age_max === '') {
        useAlert(t(`${V}.AGE_RANGE_REQUIRED`));
        return false;
      }
      if (!state.age_message.trim()) {
        useAlert(t(`${V}.AGE_MESSAGE_REQUIRED`));
        return false;
      }
      break;
    default:
      break;
  }
  return true;
};

const addMenuOption = () => {
  state.menu_options.push({
    action: 'book',
    specialties: '',
    professional: '',
    motivo: '',
    message: '',
    rule_key: '',
    labels: [],
  });
};

const removeMenuOption = idx => {
  state.menu_options.splice(idx, 1);
};

const addJourneyField = () => {
  state.journey_fields.push({ key: 'subsidiary', prompt: '' });
};

const removeJourneyField = idx => {
  state.journey_fields.splice(idx, 1);
};

const addIntakeField = () => {
  state.intake_fields.push({ key: '', prompt: '' });
};

const removeIntakeField = idx => {
  state.intake_fields.splice(idx, 1);
};

const handleSubmit = () => {
  if (!validate()) return;
  emit('submit', {
    rule_type: state.rule_type,
    title: state.title,
    active: state.active,
    rule_data: buildRuleData(),
  });
};

const open = () => dialogRef.value?.open();
const close = () => dialogRef.value?.close();

onMounted(() => {
  dialogRef.value?.open();
});

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="
      isEditing
        ? $t('INTEGRATION_SETTINGS.RAS.RULE_FORM.EDIT_TITLE')
        : $t('INTEGRATION_SETTINGS.RAS.RULE_FORM.CREATE_TITLE')
    "
    width="3xl"
    overflow-y-auto
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="emit('cancel')"
  >
    <div class="flex flex-col gap-4 pb-2">
      <WithLabel
        :label="$t('INTEGRATION_SETTINGS.RAS.RULE_FORM.TYPE.LABEL')"
        name="rule_type"
      >
        <select
          v-model="state.rule_type"
          name="rule_type"
          :disabled="isEditing"
          class="block w-full px-3 py-2 pr-6 mb-0 border-0 shadow-sm appearance-none rounded-xl select-caret leading-6"
          :class="
            isEditing ? 'opacity-60 cursor-not-allowed' : 'text-n-slate-12'
          "
        >
          <option v-for="type in RULE_TYPES" :key="type" :value="type">
            {{ type }}
          </option>
        </select>
      </WithLabel>

      <Input
        v-model="state.title"
        name="title"
        :label="$t('INTEGRATION_SETTINGS.RAS.RULE_FORM.TITLE.LABEL')"
        :placeholder="
          $t('INTEGRATION_SETTINGS.RAS.RULE_FORM.TITLE.PLACEHOLDER')
        "
      />

      <div class="flex items-center gap-2">
        <input
          id="rule-active"
          v-model="state.active"
          type="checkbox"
          class="w-4 h-4 rounded border-n-weak text-n-brand"
        />
        <label for="rule-active" class="text-sm text-n-slate-12 cursor-pointer">
          {{ $t('INTEGRATION_SETTINGS.RAS.RULE_FORM.ACTIVE.LABEL') }}
        </label>
      </div>

      <hr class="border-n-weak" />

      <!-- INFO -->
      <template v-if="state.rule_type === 'info'">
        <Input
          v-model="state.trigger"
          name="trigger"
          :label="$t('INTEGRATION_SETTINGS.RAS.RULE_FORM.TRIGGER.LABEL')"
          :placeholder="
            $t('INTEGRATION_SETTINGS.RAS.RULE_FORM.TRIGGER.PLACEHOLDER')
          "
        >
          <span class="text-xs text-n-slate-9">
            ({{ $t('INTEGRATION_SETTINGS.RAS.RULE_FORM.OPTIONAL') }})
          </span>
        </Input>
        <TextArea
          v-model="state.content"
          :label="$t('INTEGRATION_SETTINGS.RAS.RULE_FORM.CONTENT.LABEL')"
          :placeholder="
            $t('INTEGRATION_SETTINGS.RAS.RULE_FORM.CONTENT.PLACEHOLDER')
          "
          resize
          custom-text-area-class="min-h-[8rem]"
        />
      </template>

      <!-- MENU -->
      <template v-if="state.rule_type === 'menu'">
        <Input
          v-model="state.trigger"
          name="trigger"
          :label="$t('INTEGRATION_SETTINGS.RAS.RULE_FORM.TRIGGER.LABEL')"
          :placeholder="
            $t('INTEGRATION_SETTINGS.RAS.RULE_FORM.TRIGGER.PLACEHOLDER')
          "
        />
        <TextArea
          v-model="state.menu_question"
          :label="$t('INTEGRATION_SETTINGS.RAS.RULE_FORM.MENU_QUESTION.LABEL')"
          resize
          custom-text-area-class="min-h-[5rem]"
        />
        <Input
          v-model="state.menu_fallback"
          name="menu_fallback"
          :label="$t('INTEGRATION_SETTINGS.RAS.RULE_FORM.MENU_FALLBACK.LABEL')"
        />

        <div class="flex flex-col gap-2">
          <div class="flex items-center justify-between">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.RAS.RULE_FORM.MENU_OPTIONS.LABEL') }}
            </span>
            <Button
              xs
              blue
              ghost
              :label="$t('INTEGRATION_SETTINGS.RAS.RULE_FORM.MENU_OPTIONS.ADD')"
              @click="addMenuOption"
            />
          </div>

          <div
            v-for="(opt, idx) in state.menu_options"
            :key="idx"
            class="flex flex-col gap-3 p-3 border border-n-weak rounded-xl bg-n-slate-1"
          >
            <div class="flex items-center justify-between">
              <span class="text-xs font-semibold text-n-slate-10">
                {{
                  $t('INTEGRATION_SETTINGS.RAS.RULE_FORM.MENU_OPTIONS.OPTION', {
                    n: idx + 1,
                  })
                }}
              </span>
              <Button
                icon="i-lucide-x"
                xs
                slate
                ghost
                @click="removeMenuOption(idx)"
              />
            </div>

            <div class="grid grid-cols-2 gap-3">
              <WithLabel
                :label="
                  $t(
                    'INTEGRATION_SETTINGS.RAS.RULE_FORM.MENU_OPTIONS.ACTION.LABEL'
                  )
                "
                :name="`opt_action_${idx}`"
              >
                <select
                  v-model="opt.action"
                  :name="`opt_action_${idx}`"
                  class="block w-full px-3 py-2 pr-6 mb-0 border-0 shadow-sm appearance-none rounded-xl select-caret leading-6 text-n-slate-12"
                >
                  <option
                    v-for="action in MENU_ACTIONS"
                    :key="action"
                    :value="action"
                  >
                    {{ action }}
                  </option>
                </select>
              </WithLabel>

              <Input
                v-if="opt.action === 'book'"
                v-model="opt.specialties"
                :name="`opt_specialties_${idx}`"
                :label="
                  $t(
                    'INTEGRATION_SETTINGS.RAS.RULE_FORM.MENU_OPTIONS.SPECIALTIES.LABEL'
                  )
                "
                placeholder="Kinesiología, Pediatría"
              />
              <TextArea
                v-else-if="opt.action === 'info'"
                v-model="opt.message"
                :label="
                  $t(
                    'INTEGRATION_SETTINGS.RAS.RULE_FORM.MENU_OPTIONS.MESSAGE.LABEL'
                  )
                "
                custom-text-area-class="min-h-[3rem]"
              />
              <Input
                v-else-if="opt.action === 'intake'"
                v-model="opt.rule_key"
                :name="`opt_rule_key_${idx}`"
                :label="
                  $t(
                    'INTEGRATION_SETTINGS.RAS.RULE_FORM.MENU_OPTIONS.RULE_KEY.LABEL'
                  )
                "
              />
            </div>

            <div v-if="opt.action === 'book'" class="grid grid-cols-2 gap-3">
              <Input
                v-model="opt.professional"
                :name="`opt_professional_${idx}`"
                :label="
                  $t(
                    'INTEGRATION_SETTINGS.RAS.RULE_FORM.MENU_OPTIONS.PROFESSIONAL.LABEL'
                  )
                "
              />
              <Input
                v-model="opt.motivo"
                :name="`opt_motivo_${idx}`"
                :label="
                  $t(
                    'INTEGRATION_SETTINGS.RAS.RULE_FORM.MENU_OPTIONS.MOTIVO.LABEL'
                  )
                "
              />
            </div>

            <RasLabelPicker
              v-model="opt.labels"
              :label="
                $t(
                  'INTEGRATION_SETTINGS.RAS.RULE_FORM.MENU_OPTIONS.LABELS.LABEL'
                )
              "
            />
          </div>

          <p
            v-if="!state.menu_options.length"
            class="text-xs text-n-slate-9 text-center py-2"
          >
            {{ $t('INTEGRATION_SETTINGS.RAS.RULE_FORM.MENU_OPTIONS.EMPTY') }}
          </p>
        </div>
      </template>

      <!-- INTAKE -->
      <template v-if="state.rule_type === 'intake'">
        <Input
          v-model="state.trigger"
          name="trigger"
          :label="$t('INTEGRATION_SETTINGS.RAS.RULE_FORM.TRIGGER.LABEL')"
          :placeholder="
            $t('INTEGRATION_SETTINGS.RAS.RULE_FORM.TRIGGER.PLACEHOLDER')
          "
        />
        <TextArea
          v-model="state.intake_request_template"
          :label="
            $t(
              'INTEGRATION_SETTINGS.RAS.RULE_FORM.INTAKE_REQUEST_TEMPLATE.LABEL'
            )
          "
          resize
          custom-text-area-class="min-h-[8rem]"
        />

        <!-- intake_fields -->
        <div class="flex flex-col gap-2">
          <div class="flex items-center justify-between">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.RAS.RULE_FORM.INTAKE_FIELDS.LABEL') }}
            </span>
            <Button
              xs
              blue
              ghost
              :label="
                $t('INTEGRATION_SETTINGS.RAS.RULE_FORM.INTAKE_FIELDS.ADD')
              "
              @click="addIntakeField"
            />
          </div>

          <div
            v-for="(field, idx) in state.intake_fields"
            :key="idx"
            class="flex items-end gap-2 p-3 border border-n-weak rounded-xl bg-n-slate-1"
          >
            <div class="flex-1">
              <Input
                v-model="field.key"
                :name="`intake_key_${idx}`"
                label="key"
                :placeholder="
                  $t(
                    'INTEGRATION_SETTINGS.RAS.RULE_FORM.INTAKE_FIELDS.KEY_PLACEHOLDER'
                  )
                "
              />
            </div>
            <div class="flex-1">
              <Input
                v-model="field.prompt"
                :name="`intake_prompt_${idx}`"
                label="prompt"
                :placeholder="
                  $t(
                    'INTEGRATION_SETTINGS.RAS.RULE_FORM.INTAKE_FIELDS.PROMPT_PLACEHOLDER'
                  )
                "
              />
            </div>
            <Button
              icon="i-lucide-x"
              xs
              slate
              ghost
              class="mb-1"
              @click="removeIntakeField(idx)"
            />
          </div>

          <p
            v-if="!state.intake_fields.length"
            class="text-xs text-n-slate-9 text-center py-2"
          >
            {{ $t('INTEGRATION_SETTINGS.RAS.RULE_FORM.INTAKE_FIELDS.EMPTY') }}
          </p>
        </div>

        <Input
          v-model="state.intake_closing_message"
          name="intake_closing_message"
          :label="
            $t(
              'INTEGRATION_SETTINGS.RAS.RULE_FORM.INTAKE_CLOSING_MESSAGE.LABEL'
            )
          "
        />
        <Input
          v-model="state.intake_fallback"
          name="intake_fallback"
          :label="
            $t('INTEGRATION_SETTINGS.RAS.RULE_FORM.INTAKE_FALLBACK.LABEL')
          "
        />
        <RasLabelPicker
          v-model="state.intake_labels"
          :label="$t('INTEGRATION_SETTINGS.RAS.RULE_FORM.LABELS.LABEL')"
        />
      </template>

      <!-- JOURNEY -->
      <template v-if="state.rule_type === 'journey'">
        <Input
          v-model="state.trigger"
          name="trigger"
          :label="$t('INTEGRATION_SETTINGS.RAS.RULE_FORM.TRIGGER.LABEL')"
          :placeholder="
            $t('INTEGRATION_SETTINGS.RAS.RULE_FORM.TRIGGER.PLACEHOLDER')
          "
        />

        <div class="flex flex-col gap-2">
          <div class="flex items-center justify-between">
            <span class="text-sm font-medium text-n-slate-12">
              {{
                $t('INTEGRATION_SETTINGS.RAS.RULE_FORM.JOURNEY_FIELDS.LABEL')
              }}
            </span>
            <Button
              xs
              blue
              ghost
              :label="
                $t('INTEGRATION_SETTINGS.RAS.RULE_FORM.JOURNEY_FIELDS.ADD')
              "
              @click="addJourneyField"
            />
          </div>

          <div
            v-for="(field, idx) in state.journey_fields"
            :key="idx"
            class="flex items-end gap-2 p-3 border border-n-weak rounded-xl bg-n-slate-1"
          >
            <WithLabel
              label="key"
              :name="`journey_key_${idx}`"
              class="shrink-0 w-40"
            >
              <select
                v-model="field.key"
                :name="`journey_key_${idx}`"
                class="block w-full px-3 py-2 pr-6 mb-0 border-0 shadow-sm appearance-none rounded-xl select-caret leading-6 text-n-slate-12"
              >
                <option v-for="key in JOURNEY_KEYS" :key="key" :value="key">
                  {{ key }}
                </option>
              </select>
            </WithLabel>
            <div class="flex-1">
              <Input
                v-model="field.prompt"
                :name="`journey_prompt_${idx}`"
                label="prompt"
                :placeholder="
                  $t(
                    'INTEGRATION_SETTINGS.RAS.RULE_FORM.JOURNEY_FIELDS.PROMPT_PLACEHOLDER'
                  )
                "
              />
            </div>
            <Button
              icon="i-lucide-x"
              xs
              slate
              ghost
              class="mb-1"
              @click="removeJourneyField(idx)"
            />
          </div>

          <p
            v-if="!state.journey_fields.length"
            class="text-xs text-n-slate-9 text-center py-2"
          >
            {{ $t('INTEGRATION_SETTINGS.RAS.RULE_FORM.JOURNEY_FIELDS.EMPTY') }}
          </p>
        </div>

        <Input
          v-model="state.journey_inline_allowed"
          name="journey_inline_allowed"
          :label="
            $t(
              'INTEGRATION_SETTINGS.RAS.RULE_FORM.JOURNEY_INLINE_ALLOWED.LABEL'
            )
          "
          placeholder="precio, obra_social"
        />
        <TextArea
          v-model="state.journey_closing_message"
          :label="
            $t(
              'INTEGRATION_SETTINGS.RAS.RULE_FORM.JOURNEY_CLOSING_MESSAGE.LABEL'
            )
          "
          resize
          custom-text-area-class="min-h-[4rem]"
        />
      </template>

      <!-- AGE RESTRICTION -->
      <template v-if="state.rule_type === 'age_restriction'">
        <div class="grid grid-cols-2 gap-4">
          <Input
            v-model="state.age_min"
            type="number"
            name="age_min"
            :label="$t('INTEGRATION_SETTINGS.RAS.RULE_FORM.AGE_MIN.LABEL')"
          />
          <Input
            v-model="state.age_max"
            type="number"
            name="age_max"
            :label="$t('INTEGRATION_SETTINGS.RAS.RULE_FORM.AGE_MAX.LABEL')"
          />
        </div>
        <TextArea
          v-model="state.age_message"
          :label="$t('INTEGRATION_SETTINGS.RAS.RULE_FORM.AGE_MESSAGE.LABEL')"
          resize
          custom-text-area-class="min-h-[4rem]"
        />
      </template>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          faded
          slate
          :label="$t('INTEGRATION_SETTINGS.RAS.RULE_FORM.CANCEL')"
          @click="emit('cancel')"
        />
        <Button
          :label="$t('INTEGRATION_SETTINGS.RAS.RULE_FORM.SAVE')"
          :is-loading="isSaving"
          :disabled="isSaving"
          @click="handleSubmit"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
.select-caret {
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' version='1.1' width='32' height='24' viewBox='0 0 32 24'><polygon points='0,0 32,0 16,24' style='fill: rgb%28110, 111, 115%29'></polygon></svg>");
  background-origin: content-box;
  background-position: right -1rem center;
  background-repeat: no-repeat;
  background-size: 9px 6px;
}
</style>
